import { BRIEF_STATUS } from 'src/data/brief/briefStatus';
import { queryBrief, queryBriefStatus } from 'src/graphql/brief';
import { promisedWait } from 'src/lib/common';
import { AbortError } from 'src/lib/errors';
import { captureException } from 'src/lib/sentry';

const CREATION_POLLING_INTERVAL = 1000;

class BriefExtractionError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'BriefExtractionError';
    this.code = code;
    this.message = message;
  }
}

const unwrapErrors = (json) => {
  try {
    return JSON.parse(json);
  } catch (error) {
    captureException(error, {
      extra: {
        source: 'services/waitForExtraction.js -> unwrapErrors',
        value: json,
      },
    });
    return [];
  }
};

const stringifyErrors = (errors) => errors.map((e) => e.detail).join('\n');

const checkKnownBriefExtractionError = (errors, errorCode, newMessage = null) => {
  const error = errors.find((e) => e.code === errorCode);
  if (error) {
    const message = newMessage || error.detail;
    return new BriefExtractionError(error.code, message);
  }
  return null;
};

const BRIEF_EXTRACTION_ERROR = {
  clientBriefApprovalNeeded: 'CLIENT_BRIEF_APPROVAL_NEEDED',
};

const KNOWN_ERRORS = [{ code: BRIEF_EXTRACTION_ERROR.clientBriefApprovalNeeded }];

const searchForKnownBriefExtractionError = (errors) => {
  for (let i = 0; i < KNOWN_ERRORS.length; i += 1) {
    const knownErrorData = KNOWN_ERRORS[i];
    const error = checkKnownBriefExtractionError(
      errors,
      knownErrorData.code,
      knownErrorData.message
    );
    if (error) {
      return error;
    }
  }

  return null;
};

export const waitForBriefExtraction = async (briefId, options = null) => {
  // Abort process if requested
  // HINT: we don't pass "abort controller" into query itself, cause `apollo` does not work well with aborting queries
  if (options && options.abortSignal && options.abortSignal.aborted) {
    throw new AbortError();
  }

  // Check extraction status
  const result = await queryBriefStatus(briefId);

  // If extraction was successful (for now "done with errors" is treated as success) - query data
  if ([BRIEF_STATUS.doneWithErrors].includes(result.status)) {
    const errors = unwrapErrors(result.errors);
    const knownError = searchForKnownBriefExtractionError(errors);
    if (knownError) {
      return queryBrief(briefId);
    }

    const errorsText = stringifyErrors(errors);
    throw new Error(errorsText);
  }

  // Wait sometime and check again
  await promisedWait(CREATION_POLLING_INTERVAL);

  return waitForBriefExtraction(briefId, options);
};
